/* RecordsTab — Admin sub-tab combining Transcript Requests + Survey Responses.
 *
 *   • Transcripts view: searchable, status-filterable list of student
 *     transcript requests with quick status actions (Mark sent, Decline,
 *     Mark processing) and a side panel for full details + admin note.
 *   • Surveys view: list of surveys with response counts, click into a
 *     survey to see all per-student responses (collapsible). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FileText, ClipboardList, RefreshCw, Search, Send, Check, X, Clock,
  ChevronDown, ChevronRight, Mail, MapPin, ArrowLeft, Receipt,
} from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const STATUS = {
  pending:    { Icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pending' },
  processing: { Icon: Send,  cls: 'bg-sky-50 text-sky-700 border-sky-200',         label: 'Processing' },
  sent:       { Icon: Check, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Sent' },
  declined:   { Icon: X,     cls: 'bg-red-50 text-red-700 border-red-200',         label: 'Declined' },
};

export default function RecordsTab() {
  const [view, setView] = useState('transcripts'); // 'transcripts' | 'surveys' | 'invoices'

  return (
    <div data-testid="academy-admin-records-tab">
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-5 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#c9a227]" />
          <span className="text-[13px] font-bold tracking-[0.18em] uppercase text-[#0e0e10]">Records</span>
        </div>
        <div className="inline-flex rounded-full border border-[#E5E5E7] overflow-hidden">
          <button type="button" onClick={() => setView('transcripts')}
            data-testid="records-view-transcripts"
            className={`px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5
              ${view === 'transcripts' ? 'bg-[#0e0e10] text-white' : 'text-[#6E6E73]'}`}>
            <FileText className="w-3.5 h-3.5" /> Transcripts
          </button>
          <button type="button" onClick={() => setView('invoices')}
            data-testid="records-view-invoices"
            className={`px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5
              ${view === 'invoices' ? 'bg-[#0e0e10] text-white' : 'text-[#6E6E73]'}`}>
            <Receipt className="w-3.5 h-3.5" /> Invoice requests
          </button>
          <button type="button" onClick={() => setView('surveys')}
            data-testid="records-view-surveys"
            className={`px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5
              ${view === 'surveys' ? 'bg-[#0e0e10] text-white' : 'text-[#6E6E73]'}`}>
            <ClipboardList className="w-3.5 h-3.5" /> Surveys
          </button>
        </div>
      </div>

      {view === 'transcripts' && <TranscriptsView />}
      {view === 'invoices'    && <InvoicesView />}
      {view === 'surveys'     && <SurveysView />}
    </div>
  );
}

/* -------------------- Transcripts -------------------- */
function TranscriptsView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [picked, setPicked] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get('/api/academy/admin/transcript-requests', { params });
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, [filterStatus, search]);
  useEffect(() => { reload(); }, [reload]);

  const onAction = async (req, status) => {
    let note = null;
    if (status === 'declined') {
      note = window.prompt('Optional reason to share with the student:') || '';
    }
    await api.post(`/api/academy/admin/transcript-requests/${req.id}/status`, { status, admin_note: note });
    await reload();
    if (picked?.id === req.id) {
      setPicked({ ...req, status, admin_note: note ?? picked.admin_note });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5">
      <div>
        <div className="bg-white rounded-2xl border border-[#E5E5E7] p-4 mb-4 flex flex-wrap items-center gap-3">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="records-transcripts-filter-status"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227]">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="sent">Sent</option>
            <option value="declined">Declined</option>
          </select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#6E6E73] absolute left-3 top-2.5" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search student / recipient"
              data-testid="records-transcripts-search"
              className="text-[12px] rounded-full border border-[#E5E5E7] pl-8 pr-3 py-1.5 outline-none focus:border-[#c9a227] w-[260px]" />
          </div>
          <button onClick={reload} className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#6E6E73] hover:text-[#c9a227]"
            data-testid="records-transcripts-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-[#6E6E73] text-[13px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
            <FileText className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
            <div className="text-[14px] font-bold text-[#1D1D1F]">No transcript requests</div>
            <p className="text-[12.5px]">Student-submitted requests will show up here.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => {
              const meta = STATUS[r.status] || STATUS.pending;
              const Icon = meta.Icon;
              const isPicked = picked?.id === r.id;
              return (
                <li key={r.id} data-testid={`records-transcript-${r.id}`}>
                  <button type="button" onClick={() => setPicked(r)}
                    className={`w-full text-left rounded-2xl border bg-white p-4 flex items-start gap-3 transition-all
                      ${isPicked ? 'border-[#c9a227]' : 'border-[#E5E5E7] hover:border-[#c9a227]/40'}`}>
                    <span className={`inline-flex h-9 w-9 shrink-0 rounded-xl items-center justify-center
                      ${r.delivery_method === 'email' ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'}`}>
                      {r.delivery_method === 'email' ? <Mail className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[13.5px] font-bold text-[#0e0e10]">
                          {r.user_name} · <span className="font-mono text-[12px] font-normal text-[#6E6E73]">{r.user_email}</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      <div className="text-[12px] text-[#6E6E73] mt-0.5">
                        To <strong>{r.recipient_name}</strong> ·{' '}
                        {r.delivery_method === 'email'
                          ? <span className="font-mono">{r.recipient_email}</span>
                          : <span>{r.recipient_address}</span>}
                      </div>
                      <div className="text-[11px] text-[#9aa0a6] mt-0.5">
                        {new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                        {r.purpose && <> · {r.purpose}</>}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Detail panel */}
      <aside className="bg-white rounded-2xl border border-[#E5E5E7] p-5 h-fit lg:sticky lg:top-6">
        {!picked ? (
          <div className="text-center text-[#6E6E73] py-10">
            <FileText className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
            <div className="text-[13.5px] font-bold text-[#0e0e10]">Pick a request</div>
            <p className="text-[12px]">Click any row to see details and take action.</p>
          </div>
        ) : (
          <div data-testid={`records-transcript-detail-${picked.id}`}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">Request detail</span>
              <button type="button" onClick={() => setPicked(null)}
                className="text-[#6E6E73] hover:text-[#0e0e10]" aria-label="Close detail">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-[13px]">
              <DetailRow label="Student" value={picked.user_name} mono={false} />
              <DetailRow label="Email"   value={picked.user_email} mono />
              <DetailRow label="Sent to" value={picked.recipient_name} mono={false} />
              <DetailRow label="Delivery" value={picked.delivery_method === 'email' ? `Email · ${picked.recipient_email}` : `Post · ${picked.recipient_address}`} />
              {picked.purpose && <DetailRow label="Purpose" value={picked.purpose} />}
              {picked.program_slug && <DetailRow label="Program" value={picked.program_slug} mono />}
              <DetailRow label="Submitted" value={new Date(picked.created_at).toLocaleString()} />
              {picked.admin_note && <DetailRow label="Admin note" value={picked.admin_note} />}
            </div>
            <div className="mt-5 pt-4 border-t border-[#E5E5E7]">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a] mb-2">Actions</div>
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn onClick={() => onAction(picked, 'processing')} disabled={picked.status === 'processing'}
                  testId={`records-action-processing-${picked.id}`}
                  className="border-sky-200 text-sky-700 hover:bg-sky-50">
                  <Send className="w-3.5 h-3.5" /> Mark processing
                </ActionBtn>
                <ActionBtn onClick={() => onAction(picked, 'sent')} disabled={picked.status === 'sent'}
                  testId={`records-action-sent-${picked.id}`}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <Check className="w-3.5 h-3.5" /> Mark sent
                </ActionBtn>
                <ActionBtn onClick={() => onAction(picked, 'pending')} disabled={picked.status === 'pending'}
                  testId={`records-action-pending-${picked.id}`}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50">
                  <Clock className="w-3.5 h-3.5" /> Reset to pending
                </ActionBtn>
                <ActionBtn onClick={() => onAction(picked, 'declined')} disabled={picked.status === 'declined'}
                  testId={`records-action-declined-${picked.id}`}
                  className="border-red-200 text-red-700 hover:bg-red-50">
                  <X className="w-3.5 h-3.5" /> Decline
                </ActionBtn>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function ActionBtn({ children, className = '', disabled, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-[11.5px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed ${className}`}>
      {children}
    </button>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-[80px] shrink-0 text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#a8871a] mt-0.5">{label}</span>
      <span className={`flex-1 text-[13px] text-[#0e0e10] ${mono ? 'font-mono text-[12.5px] break-all' : ''}`}>{value || '—'}</span>
    </div>
  );
}

/* -------------------- Surveys -------------------- */
function SurveysView() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null); // { survey, rows }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/academy/admin/surveys');
      setSurveys(data.rows || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const open = async (s) => {
    const { data } = await api.get(`/api/academy/admin/surveys/${s.id}/responses`);
    setPicked({ survey: data.survey, rows: data.rows });
  };

  if (picked) {
    return <SurveyResponses {...picked} onBack={() => setPicked(null)} />;
  }

  return (
    <div>
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-4 mb-4 flex items-center gap-3">
        <span className="text-[12px] text-[#6E6E73]">{surveys.length} survey{surveys.length === 1 ? '' : 's'}</span>
        <button onClick={reload} className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#6E6E73] hover:text-[#c9a227]"
          data-testid="records-surveys-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-[#6E6E73] text-[13px]">Loading…</div>
      ) : surveys.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
          <ClipboardList className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
          <div className="text-[14px] font-bold text-[#1D1D1F]">No surveys yet</div>
          <p className="text-[12.5px]">Surveys auto-seed on first student visit.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {surveys.map((s) => (
            <li key={s.id} data-testid={`records-survey-${s.id}`}>
              <button type="button" onClick={() => open(s)}
                className="w-full text-left rounded-2xl border border-[#E5E5E7] bg-white p-4 hover:border-[#c9a227]/50 transition-colors flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 rounded-xl bg-[#fff8e7] text-[#a8871a] items-center justify-center">
                  <ClipboardList className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[14px] font-extrabold text-[#0e0e10]">{s.title}</div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
                      {s.response_count} response{s.response_count === 1 ? '' : 's'}
                    </span>
                  </div>
                  {s.subtitle && <div className="text-[12.5px] text-[#6E6E73] mt-0.5">{s.subtitle}</div>}
                  <div className="text-[11px] text-[#9aa0a6] mt-1">
                    {(s.questions || []).length} questions · {s.estimated_minutes ? `${s.estimated_minutes} min` : 'open'}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#6E6E73] mt-1.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SurveyResponses({ survey, rows, onBack }) {
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };
  const questionMap = useMemo(() => {
    const m = {};
    (survey.questions || []).forEach((q) => { m[q.id] = q.label; });
    return m;
  }, [survey]);

  return (
    <div data-testid={`records-survey-detail-${survey.id}`}>
      <button type="button" onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[12.5px] text-[#6E6E73] hover:text-[#c9a227] mb-3"
        data-testid="records-survey-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to surveys
      </button>
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-5 mb-4">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a] mb-1">Survey</div>
        <div className="text-[18px] font-extrabold text-[#0e0e10]">{survey.title}</div>
        {survey.subtitle && <div className="text-[12.5px] text-[#6E6E73] mt-0.5">{survey.subtitle}</div>}
        <div className="text-[11px] text-[#9aa0a6] mt-2">
          {rows.length} response{rows.length === 1 ? '' : 's'} · {(survey.questions || []).length} questions
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
          <ClipboardList className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
          <div className="text-[14px] font-bold text-[#1D1D1F]">No responses yet</div>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const isOpen = expanded.has(r.id);
            return (
              <li key={r.id} data-testid={`records-survey-response-${r.id}`}
                className="rounded-2xl border border-[#E5E5E7] bg-white overflow-hidden">
                <button type="button" onClick={() => toggle(r.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left">
                  <span className="inline-flex h-8 w-8 rounded-xl bg-[#fff8e7] text-[#a8871a] items-center justify-center text-[11px] font-extrabold">
                    {(r.user_email || '??').slice(0, 2).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-[#0e0e10] font-mono">{r.user_email}</div>
                    <div className="text-[11px] text-[#6E6E73]">
                      Submitted {new Date(r.submitted_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#6E6E73] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-0 border-t border-[#E5E5E7] space-y-2">
                    {Object.entries(r.answers || {}).map(([qid, ans]) => (
                      <div key={qid} className="bg-[#FAFAFA] rounded-lg px-3 py-2">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#a8871a] mb-0.5">
                          {questionMap[qid] || qid}
                        </div>
                        <div className="text-[12.5px] text-[#0e0e10] whitespace-pre-wrap">{ans || <em className="text-[#9aa0a6]">(no answer)</em>}</div>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


/* -------------------- Invoice Requests -------------------- */
function InvoicesView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [picked, setPicked] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get('/api/academy/admin/invoice-requests', { params });
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, [filterStatus, search]);
  useEffect(() => { reload(); }, [reload]);

  const onAction = async (req, status) => {
    let note = null;
    if (status === 'declined') {
      note = window.prompt('Optional reason to share with the student:') || '';
    }
    await api.post(`/api/academy/admin/invoice-requests/${req.id}/status`, { status, admin_note: note });
    await reload();
    if (picked?.id === req.id) {
      setPicked({ ...req, status, admin_note: note ?? picked.admin_note });
    }
  };

  const fmt = (n, cur = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Number(n) || 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5">
      <div>
        <div className="bg-white rounded-2xl border border-[#E5E5E7] p-4 mb-4 flex flex-wrap items-center gap-3">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="records-invoices-filter-status"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227]">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="sent">Sent</option>
            <option value="declined">Declined</option>
          </select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#6E6E73] absolute left-3 top-2.5" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search student / purpose"
              data-testid="records-invoices-search"
              className="text-[12px] rounded-full border border-[#E5E5E7] pl-8 pr-3 py-1.5 outline-none focus:border-[#c9a227] w-[260px]" />
          </div>
          <button onClick={reload} className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#6E6E73] hover:text-[#c9a227]"
            data-testid="records-invoices-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-[#6E6E73] text-[13px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
            <Receipt className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
            <div className="text-[14px] font-bold text-[#1D1D1F]">No invoice requests</div>
            <p className="text-[12.5px]">Student- and parent-submitted invoice requests will show up here.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => {
              const meta = STATUS[r.status] || STATUS.pending;
              const Icon = meta.Icon;
              const isPicked = picked?.id === r.id;
              return (
                <li key={r.id} data-testid={`records-invoice-${r.id}`}>
                  <button type="button" onClick={() => setPicked(r)}
                    className={`w-full text-left rounded-2xl border bg-white p-4 flex items-start gap-3 transition-all
                      ${isPicked ? 'border-[#c9a227]' : 'border-[#E5E5E7] hover:border-[#c9a227]/40'}`}>
                    <span className="inline-flex h-9 w-9 shrink-0 rounded-xl bg-[#fff8e7] text-[#a8871a] items-center justify-center">
                      <Receipt className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[13.5px] font-bold text-[#0e0e10]">
                          {r.user_name} · <span className="font-mono text-[12px] font-normal text-[#6E6E73]">{r.user_email}</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      <div className="text-[12.5px] text-[#0e0e10] mt-0.5 font-bold">
                        {fmt(r.amount, r.currency)} · {r.purpose}
                      </div>
                      <div className="text-[11px] text-[#9aa0a6] mt-0.5">
                        {new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                        {r.program_slug && <> · {r.program_slug}</>}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <aside className="bg-white rounded-2xl border border-[#E5E5E7] p-5 h-fit lg:sticky lg:top-6">
        {!picked ? (
          <div className="text-center text-[#6E6E73] py-10">
            <Receipt className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
            <div className="text-[13.5px] font-bold text-[#0e0e10]">Pick a request</div>
            <p className="text-[12px]">Click any row to see details and take action.</p>
          </div>
        ) : (
          <div data-testid={`records-invoice-detail-${picked.id}`}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">Invoice request</span>
              <button type="button" onClick={() => setPicked(null)}
                className="text-[#6E6E73] hover:text-[#0e0e10]" aria-label="Close detail">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-[13px]">
              <DetailRow label="Student" value={picked.user_name} />
              <DetailRow label="Email"   value={picked.user_email} mono />
              <DetailRow label="Amount"  value={fmt(picked.amount, picked.currency)} />
              <DetailRow label="Purpose" value={picked.purpose} />
              {picked.program_slug && <DetailRow label="Program" value={picked.program_slug} mono />}
              {picked.payer_name  && picked.payer_name !== picked.user_name &&
                <DetailRow label="Payer" value={`${picked.payer_name} · ${picked.payer_email || ''}`} />}
              {picked.notes && <DetailRow label="Notes" value={picked.notes} />}
              <DetailRow label="Submitted" value={new Date(picked.created_at).toLocaleString()} />
              {picked.admin_note && <DetailRow label="Admin note" value={picked.admin_note} />}
            </div>
            <div className="mt-5 pt-4 border-t border-[#E5E5E7]">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a] mb-2">Actions</div>
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn onClick={() => onAction(picked, 'processing')} disabled={picked.status === 'processing'}
                  testId={`records-invoice-action-processing-${picked.id}`}
                  className="border-sky-200 text-sky-700 hover:bg-sky-50">
                  <Send className="w-3.5 h-3.5" /> Mark processing
                </ActionBtn>
                <ActionBtn onClick={() => onAction(picked, 'sent')} disabled={picked.status === 'sent'}
                  testId={`records-invoice-action-sent-${picked.id}`}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <Check className="w-3.5 h-3.5" /> Mark sent
                </ActionBtn>
                <ActionBtn onClick={() => onAction(picked, 'pending')} disabled={picked.status === 'pending'}
                  testId={`records-invoice-action-pending-${picked.id}`}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50">
                  <Clock className="w-3.5 h-3.5" /> Reset to pending
                </ActionBtn>
                <ActionBtn onClick={() => onAction(picked, 'declined')} disabled={picked.status === 'declined'}
                  testId={`records-invoice-action-declined-${picked.id}`}
                  className="border-red-200 text-red-700 hover:bg-red-50">
                  <X className="w-3.5 h-3.5" /> Decline
                </ActionBtn>
              </div>
              <a
                href={`mailto:${picked.user_email}?subject=${encodeURIComponent('Your ZHELTA invoice — ' + picked.purpose)}&body=${encodeURIComponent('Hi ' + (picked.user_name || '') + ',\n\nPlease find your invoice attached for ' + picked.purpose + ' (' + fmt(picked.amount, picked.currency) + ').\n\n— ZHELTA Finance')}`}
                data-testid={`records-invoice-email-${picked.id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11.5px] font-bold uppercase tracking-wider text-[#0e0e10]"
                style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
                <Mail className="w-3.5 h-3.5" /> Open email draft
              </a>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
